import React, {useEffect} from "react";
import useTranslate from "../../../../core/languages/hooks/useTranslate";
import {Controller, useFormContext} from "react-hook-form";
import EventCity from "../../../../eventCities/types/EventCity";
import {
  ArrivalTransportSelect,
  DepartureTransportSelect,
  TransportEnum
} from "../../../../transfers/enums/TransferTransportEnum";
import {TransferDirectionEnum} from "../../../../transfers/enums/TransferDirectionEnum";
import TransportDate from "../../../../transfers/components/dateAndTimeComponents/TransportDate";
import TransportTime from "../../../../transfers/components/dateAndTimeComponents/TransportTime";
import NumberOfPassengersField from "../../../../transfers/components/passengerComponents/NumberOfPassengersField";
import ArrivalTransportCompanyAndNumber
  from "../../../../transfers/components/referenceComponents/ArrivalTransportCompanyAndNumber";
import ArrivalLocationFrom from "../../../../transfers/components/locationComponents/ArrivalLocationFrom";
import ArrivalLocationTo from "../../../../transfers/components/locationComponents/ArrivalLocationTo";
import DepartureTransportCompanyAndNumber
  from "../../../../transfers/components/referenceComponents/DepartureTransportCompanyAndNumber";
import DepartureLocationFrom from "../../../../transfers/components/locationComponents/DepartureLocationFrom";
import DepartureLocationTo from "../../../../transfers/components/locationComponents/DepartureLocationTo";
import {Alert} from "../../../../ui/shared/Alert/Alert";
import VehiclePricesSelector from "../../../../transfers/components/vehiclePricesComponents/VehiclePricesSelector";
import useTransferRouteGetArrival from "../../../../transfers/hooks/useTransferRouteGetArrival";
import useTransferRouteGetDeparture from "../../../../transfers/hooks/useTransferRouteGetDeparture";
import CustomRouteAlert from "../../../../common/components/CustomRouteAlert";
import TermsAndConditions from "../../../../transfers/components/form/TermsAndConditions";
import {hasAnyOptionalTransferValue} from "../../../utils/optionalTransferStepValidation";
import {WizardSaveInput} from "../../../types/WizardSaveInput";


interface StepTransferDetailsProps {
  eventCity?: EventCity;
}

const StepOtherTransferDetails: React.FC<StepTransferDetailsProps> = ({eventCity}) => {
  const t = useTranslate();
  const {control, watch, setValue, formState: {errors}} = useFormContext();

  const direction = watch("direction");
  const city = watch("city");
  const arrivalTransport = watch("arrivalTransport");
  const departureTransport = watch("departureTransport");
  const arrivalLocationFrom = watch("arrivalLocationFrom");
  const arrivalLocationTo = watch("arrivalLocationTo");
  const departureLocationFrom = watch("departureLocationFrom");
  const departureLocationTo = watch("departureLocationTo");

  // Informational only — validation/advance is owned by PackageWizard
  const formValues = watch() as WizardSaveInput;
  const hasStartedOptionalTransfer = hasAnyOptionalTransferValue(formValues, direction);

  // Get transfer routes - ARRIVAL
  const {route: arrivalRoute, isLoading: arrivalRouteLoading} = useTransferRouteGetArrival({
    cityId: city, arrivalLocationFrom, arrivalLocationTo, execute: direction !== TransferDirectionEnum.ARRIVAL,
  });

  // Get transfer routes - DEPARTURE
  const {route: departureRoute, isLoading: departureRouteLoading} = useTransferRouteGetDeparture({
    cityId: city, departureLocationFrom, departureLocationTo, execute: direction !== TransferDirectionEnum.DEPARTURE,
  });

  // Clear reference fields when transport is OTHER
  useEffect(() => {
    if (arrivalTransport === TransportEnum.OTHER) {
      setValue("arrivalTransportReference", "");
      setValue("arrivalTransportReferenceNumber", "");
    }
  }, [arrivalTransport, setValue]);

  useEffect(() => {
    if (departureTransport === TransportEnum.OTHER) {
      setValue("departureTransportReference", "");
      setValue("departureTransportReferenceNumber", "");
    }
  }, [departureTransport, setValue]);

  // Clear vehicle when ARRIVAL transport changes
  useEffect(() => {
    setValue("arrivalVehicle", "");
  }, [arrivalTransport, setValue]);

  // Clear vehicle and route when ARRIVAL locations change
  useEffect(() => {
    setValue("arrivalVehicle", "");
    setValue("arrivalRoute", null);
  }, [arrivalLocationFrom, arrivalLocationTo, setValue]);

  // Clear vehicle when DEPARTURE transport changes
  useEffect(() => {
    setValue("departureVehicle", "");
  }, [departureTransport, setValue]);

  // Clear vehicle and route when DEPARTURE locations change
  useEffect(() => {
    setValue("departureVehicle", "");
    setValue("departureRoute", null);
  }, [departureLocationFrom, departureLocationTo, setValue]);

  // Show alert only if: (is OTHER or no routes) AND both locations are selected AND is individuals AND NOT loading
  const showArrivalCustomRouteAlert =
    !arrivalRouteLoading &&
    Number(arrivalTransport) === TransportEnum.OTHER &&
    !arrivalRoute?.supplierRouteVehiclePrices?.length &&
    arrivalLocationFrom && arrivalLocationTo;

  const showDepartureRouteAlert =
    !departureRouteLoading &&
    Number(departureTransport) === TransportEnum.OTHER &&
    !departureRoute?.supplierRouteVehiclePrices?.length &&
    departureLocationFrom && departureLocationTo;

  return (
    <div className="space-y-6">
      {/* --- TRANSFER DETAILS HEADER --- */}
      <div className="pb-4 border-b border-neutral-200 dark:border-neutral-700">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          {t("transfers.fields.transferDetails")}
        </h3>
      </div>
      {/* Success Banner */}
      <Alert type="warning" closable={false} className="mb-6 border-l-4 border-orange-500">
        <span className="font-medium">
          {t("packages.message.canBookAnotherTransfer")}
        </span>
      </Alert>
      {hasStartedOptionalTransfer && (
        <Alert type="info" closable={false} className="mb-6 border-l-4 border-blue-500">
          <span className="font-medium">
            {t("packages.message.optionalTransferAllOrNothing")}
          </span>
        </Alert>
      )}
      {/* --- TYPE OF TRANSFER --- */}

      {direction !== TransferDirectionEnum.ARRIVAL && (
        <div className="space-y-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <div className="pb-4">
            <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {t("transfers.fields.oneWayTransfer")}
            </h4>
          </div>
          <ArrivalTransportSelect control={control} errors={errors} t={t}/>
          {/* Transport Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TransportDate name="arrivalTransportDate" transportFieldName="arrivalTransport" direction="arrival"
                           eventCity={eventCity}/>
            <TransportTime name="arrivalTransportTime" transportFieldName="arrivalTransport" direction="arrival" dateFieldName="arrivalTransportDate"/>
            <NumberOfPassengersField name="arrivalNumberOfPassengers" direction="arrival"/>
          </div>
          {/* Transport Reference Fields */}
          {Number(arrivalTransport) !== TransportEnum.OTHER && (
            <ArrivalTransportCompanyAndNumber/>
          )}
          {/* Location Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ArrivalLocationFrom/>
            <ArrivalLocationTo/>
          </div>
          {/* Vehicle Selection */}
          {arrivalRoute?.supplierRouteVehiclePrices
            && arrivalRoute?.supplierRouteVehiclePrices?.length > 0
            && Number(arrivalTransport) !== TransportEnum.OTHER && (
              <div className="pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <VehiclePricesSelector
                  vehicles={arrivalRoute?.supplierRouteVehiclePrices || []}
                  fieldName="arrivalVehicle"
                  label={t("transfers.message.selectVehicleArrival")}
                  direction={TransferDirectionEnum.ARRIVAL}
                />
              </div>
            )}
          {showArrivalCustomRouteAlert && (
            <CustomRouteAlert/>
          )}
        </div>
      )}
      {direction !== TransferDirectionEnum.DEPARTURE && (
        <div className="space-y-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <div className="pb-4">
            <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {t("transfers.fields.returnTransfer")}
            </h4>
          </div>
          {/* Transport Type */}
          <DepartureTransportSelect control={control} errors={errors} t={t}/>
          {/* Transport Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TransportDate name="departureTransportDate" transportFieldName="departureTransport" direction="departure"
                           eventCity={eventCity}/>
            <TransportTime name="departureTransportTime" transportFieldName="departureTransport" direction="departure" dateFieldName="departureTransportDate"/>
            <NumberOfPassengersField name="departureNumberOfPassengers" direction="departure"/>
          </div>
          {/* Transport Reference Fields */}
          {Number(departureTransport) !== TransportEnum.OTHER && (
            <DepartureTransportCompanyAndNumber/>
          )}
          {/* Location Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DepartureLocationFrom/>
            <DepartureLocationTo/>
          </div>
          {/* Vehicle Selection */}
          {departureRoute?.supplierRouteVehiclePrices &&
            departureRoute?.supplierRouteVehiclePrices?.length > 0 &&
            Number(departureTransport) !== TransportEnum.OTHER && (
              <div className="pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <VehiclePricesSelector
                  vehicles={departureRoute.supplierRouteVehiclePrices || []}
                  fieldName="departureVehicle"
                  label={t("transfers.message.selectVehicleDeparture")}
                  direction={TransferDirectionEnum.DEPARTURE}
                />
              </div>
            )}
          {showDepartureRouteAlert && (
            <CustomRouteAlert/>
          )}
        </div>
      )}
      <div className="space-y-6 mt-8">
        {/* Comments */}
        <div>
          <Controller
            name="comments"
            control={control}
            render={({ field }) => (
              <textarea
                {...field}
                placeholder={t("transfers.fields.comments") || "Comments"}
                rows={4}
                className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
          />
        </div>
        <TermsAndConditions/>
      </div>
    </div>
  );
}

export default StepOtherTransferDetails;
